import React, { useCallback, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { QrCode, RefreshCw, Smartphone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { APP_SCHEME_AUTH, detectPlatform } from '../lib/appLinks';

const REFRESH_MS = 4 * 60 * 1000;

function useHandoffCode(enabled, source) {
  const { authAxios } = useAuth();
  const [code, setCode] = useState(null);
  const [error, setError] = useState(false);
  const mint = useCallback(async () => {
    try {
      const { data } = await authAxios.post('/auth/handoff', { source });
      setCode(data.code);
      setError(false);
      return data.code;
    } catch {
      setError(true);
      return null;
    }
  }, [authAxios, source]);
  useEffect(() => {
    if (!enabled) return undefined;
    mint();
    const t = setInterval(mint, REFRESH_MS);
    return () => clearInterval(t);
  }, [enabled, mint]);
  return { code, error, mint };
}

export function QrCodeCard() {
  const { user, ready } = useAuth();
  const [platform] = useState(detectPlatform);
  const isDesktop = platform === 'desktop';
  const { code, error, mint } = useHandoffCode(ready && Boolean(user) && isDesktop, 'qr');
  if (!isDesktop) return null;
  const origin = window.location.origin;
  const value = user && code ? `${origin}/app-auth?code=${code}` : `${origin}/app-auth`;
  return (
    <div data-testid="download-qr-card" className="mt-10 mx-auto max-w-md flex items-center gap-6 p-5 rounded-2xl bg-brand-cream text-brand-green text-left shadow-xl">
      <div className="shrink-0 p-2 bg-white rounded-xl" data-testid="download-qr-code" data-qr-value={value}>
        <QRCodeSVG value={value} size={120} fgColor="#1f3a2e" level="M" />
      </div>
      <div>
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-brand-green/60">
          <QrCode className="w-3.5 h-3.5" /> Scan with your phone
        </div>
        <p className="mt-2 font-serif text-xl leading-snug">
          {user ? 'Install and open the app — already signed in.' : 'Point your camera here to get the app.'}
        </p>
        {user && (
          <p data-testid="download-qr-hint" className="mt-2 text-xs text-brand-green/60">
            {error ? 'Could not create a sign-in code.' : !code ? 'Creating a one-time sign-in code…' : 'This sign-in code works once and expires in 5 minutes.'}{' '}
            <button type="button" data-testid="download-qr-refresh-btn" onClick={mint} className="inline-flex items-center gap-1 underline hover:text-brand-green">
              <RefreshCw className="w-3 h-3" /> New code
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

export function OpenInAppButton() {
  const { user } = useAuth();
  const [platform] = useState(detectPlatform);
  const { mint } = useHandoffCode(false, 'mobile_button');
  const [busy, setBusy] = useState(false);
  if (!user || platform === 'desktop') return null;
  const open = async () => {
    setBusy(true);
    const code = await mint();
    setBusy(false);
    if (code) window.location.href = `${APP_SCHEME_AUTH}?code=${encodeURIComponent(code)}`;
  };
  return (
    <button
      type="button"
      data-testid="download-open-app-signed-in-btn"
      onClick={open}
      disabled={busy}
      className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-brand-gold text-brand-green-darker font-semibold hover:scale-[1.02] transition-transform disabled:opacity-70"
    >
      <Smartphone className="w-4 h-4" /> {busy ? 'Opening…' : 'Already installed? Open the app signed in'}
    </button>
  );
}
